import React, { useEffect } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { StarRating } from 'components';
import * as yup from 'yup';
import { BaseEditModal } from '../base-edit-modal';

type WorkStyleFormData = {
  workStyles: {
    description: string;
    rating: number;
  }[];
};

const workStyleSchema = yup.object().shape({
  workStyles: yup.array().of(
    yup.object().shape({
      description: yup.string().required('Description is required'),
      rating: yup.number().min(1, 'Rating must be at least 1').max(7, 'Rating must be at most 7').required('Rating is required'),
    })
  ).required('At least one work style is required').min(4, 'All work styles are required'),
});

interface WorkStylesEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentData: WorkStyleFormData;
  onSave: (data: WorkStyleFormData) => void;
  isSubmitting?: boolean;
}

export const WorkStylesEditModal: React.FC<WorkStylesEditModalProps> = ({
  isOpen,
  onClose,
  currentData,
  onSave,
  isSubmitting
}) => {
  const defaultValues: WorkStyleFormData = currentData;

  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
    setValue,
    watch
  } = useForm<WorkStyleFormData>({
    defaultValues,
    resolver: yupResolver(workStyleSchema)
  });

  const { fields } = useFieldArray({
    control,
    name: 'workStyles'
  });

  useEffect(() => {
    reset(defaultValues);
  }, [currentData, reset]);

  const onSubmit = (data: WorkStyleFormData) => {
    onSave(data);
  };

  const handleRatingChange = (index: number, rating: number) => {
    setValue(`workStyles.${index}.rating`, rating, { shouldValidate: true });
  };

  return (
    <BaseEditModal
      isOpen={isOpen}
      onClose={onClose}
      title="Work Styles"
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isSubmitting}
    >
      <div className="font-[Gelion] w-[90%] mx-auto mt-[10px] mb-[20px]">
        <h3 className="text-lg font-semibold text-[#1D2939] mb-1">Work Styles</h3>
        <p className="mb-[20px]">
          Rate the following from highest (7) to lowest (1) as they relate to the role
        </p>

        {fields.map((item, index) => (
          <div key={item.id} className="mb-5">
            <p className="font-[Gelion] text-[#1a3c63] mb-1.5">{index + 1}. {item.description}</p>
            <div className="mt-1.5">
              <StarRating
                value={watch(`workStyles.${index}.rating`)}
                onChange={(rating) => handleRatingChange(index, rating)}
                count={7}
                startAt={1}
              />
            </div>
            {errors.workStyles?.[index]?.rating?.message && (
              <small className="text-xs text-[#e74444] block mb-3">{errors.workStyles?.[index]?.rating?.message}</small>
            )}
          </div>
        ))}
      </div>
    </BaseEditModal>
  );
};
